import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';

type Step = 1 | 2 | 3;

const RESEND_COOLDOWN = 60;

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef<number | null>(null);
  const [otpEmailEnabled, setOtpEmailEnabled] = useState(true);
  const [otpSmsEnabled, setOtpSmsEnabled] = useState(false);
  const [channel, setChannel] = useState<'email' | 'sms'>('email');
  const [channelUsed, setChannelUsed] = useState<'email' | 'sms'>('sms');
  const [maskedEmail, setMaskedEmail] = useState('');

  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = window.setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [cooldown]);

  // قنوات OTP المفعّلة
  useEffect(() => {
    api.get('/app-config').then((res) => {
      const otp = res.data?.data?.otp || {};
      const e = otp.email_enabled !== false;
      const sm = !!otp.sms_enabled;
      setOtpEmailEnabled(e);
      setOtpSmsEnabled(sm);
      setChannel(e ? 'email' : (sm ? 'sms' : 'email'));
    }).catch(() => {});
  }, []);

  const handleApiError = (err: any, fallback: string) => {
    const status = err?.response?.status;
    const msg = err?.response?.data?.message;
    const remaining = err?.response?.data?.errors?.remaining_attempts;
    if (typeof remaining === 'number') setRemainingAttempts(remaining);
    if (status === 429) {
      toast.error('حاول بعد قليل');
      return;
    }
    toast.error(msg || fallback);
  };

  const requestOtp = async (isResend = false) => {
    if (!/^05[0-9]{8}$/.test(phone)) {
      toast.error('رقم الجوال غير صالح (05XXXXXXXX)');
      return;
    }
    try {
      setLoading(true);
      const res = await api.post('/auth/forgot-password', { phone, channel });
      const d = res.data?.data || {};
      const used = (d.channel === 'sms' || d.channel === 'email') ? d.channel : channel;
      setChannelUsed(used);
      setMaskedEmail(d.masked_email || '');
      toast.success(isResend
        ? 'تم إعادة إرسال الرمز'
        : (used === 'sms' ? 'تم إرسال رمز التحقق إلى جوالك' : 'تم إرسال رمز التحقق إلى بريدك'));
      setRemainingAttempts(null);
      setCooldown(RESEND_COOLDOWN);
      if (!isResend) setStep(2);
    } catch (err: any) {
      handleApiError(err, 'تعذر إرسال الرمز، حاول لاحقاً');
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^[0-9]{4,6}$/.test(code)) {
      toast.error('رمز التحقق يجب أن يكون من 4 إلى 6 أرقام');
      return;
    }
    setStep(3);
  };

  const submitReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }
    if (password !== passwordConfirm) {
      toast.error('كلمتا المرور غير متطابقتين');
      return;
    }
    try {
      setLoading(true);
      await api.post('/auth/reset-password', {
        phone,
        code,
        password,
        password_confirmation: passwordConfirm,
      });
      toast.success('تم تغيير كلمة المرور، سجّل الدخول الآن');
      navigate('/login');
    } catch (err: any) {
      handleApiError(err, 'تعذر تغيير كلمة المرور');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setRemainingAttempts(null);
    if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
  };

  const steps = [
    { n: 1, label: 'رقم الجوال' },
    { n: 2, label: 'رمز التحقق' },
    { n: 3, label: 'كلمة المرور' },
  ];

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-sm p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-primary-500 mb-2">الجلب</h1>
          <p className="text-gray-500 text-sm">استعادة كلمة المرور</p>
        </div>

        <div className="flex items-center justify-between mb-8 px-2">
          {steps.map((s, i) => {
            const isActive = step === s.n;
            const isDone = step > s.n;
            return (
              <React.Fragment key={s.n}>
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={
                      'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition ' +
                      (isDone
                        ? 'bg-primary-500 text-white'
                        : isActive
                        ? 'bg-primary-500 text-white ring-4 ring-primary-100'
                        : 'bg-gray-200 text-gray-500')
                    }
                  >
                    {isDone ? '✓' : s.n}
                  </div>
                  <span className={'text-[11px] mt-2 ' + (isActive || isDone ? 'text-primary-500 font-semibold' : 'text-gray-400')}>
                    {s.label}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div className={'h-[2px] flex-1 mx-1 mb-5 ' + (step > s.n ? 'bg-primary-500' : 'bg-gray-200')} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {step === 1 && (
          <form onSubmit={(e) => { e.preventDefault(); requestOtp(false); }}>
            <h2 className="text-lg font-bold text-gray-800 mb-2 text-center">إدخال رقم الجوال</h2>
            <p className="text-xs text-gray-500 text-center mb-6">سنرسل لك رمز تحقق لاستعادة كلمة المرور</p>

            <div className="mb-6">
              <label className="block text-sm text-gray-600 mb-2">رقم الجوال</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="05XXXXXXXX"
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                dir="ltr"
                inputMode="numeric"
                autoFocus
              />
            </div>

            {otpEmailEnabled && otpSmsEnabled && (
              <div className="mb-6">
                <label className="block text-sm text-gray-600 mb-2">استلام رمز التحقق عبر</label>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" onClick={() => setChannel('email')}
                    className={`border rounded-lg px-4 py-3 text-sm font-semibold transition ${channel === 'email' ? 'border-primary-500 bg-primary-50 text-primary-600' : 'border-gray-200 text-gray-500'}`}>
                    ✉️ الإيميل
                  </button>
                  <button type="button" onClick={() => setChannel('sms')}
                    className={`border rounded-lg px-4 py-3 text-sm font-semibold transition ${channel === 'sms' ? 'border-primary-500 bg-primary-50 text-primary-600' : 'border-gray-200 text-gray-500'}`}>
                    📱 الجوال
                  </button>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-500 text-white py-3 rounded-lg font-semibold hover:bg-primary-600 transition disabled:opacity-50"
            >
              {loading ? 'جاري الإرسال...' : 'إرسال رمز التحقق'}
            </button>

            <p className="text-center text-sm text-gray-400 mt-6">
              تذكرت كلمة المرور؟{' '}
              <Link to="/login" className="text-primary-500 font-semibold">تسجيل الدخول</Link>
            </p>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={submitOtp}>
            <h2 className="text-lg font-bold text-gray-800 mb-2 text-center">أدخل رمز التحقق</h2>
            <p className="text-xs text-gray-500 text-center mb-6">
              أرسلنا رمزاً إلى <span className="font-semibold text-gray-700" dir="ltr">{channelUsed === 'email' && maskedEmail ? maskedEmail : phone}</span>
            </p>

            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">رمز التحقق</label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="••••"
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-center text-lg font-bold tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-primary-500"
                dir="ltr"
                inputMode="numeric"
                autoFocus
              />
            </div>

            {remainingAttempts !== null && (
              <p className="text-xs text-red-600 mb-4 text-center">
                المحاولات المتبقية: {remainingAttempts}
              </p>
            )}

            <div className="flex items-center justify-between text-xs mb-6">
              <button
                type="button"
                onClick={() => requestOtp(true)}
                disabled={cooldown > 0 || loading}
                className="text-primary-500 font-semibold hover:text-primary-600 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {cooldown > 0 ? `إعادة الإرسال بعد ${cooldown} ث` : 'إعادة إرسال الرمز'}
              </button>
              <span className="text-gray-400">لم يصلك الرمز؟</span>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-500 text-white py-3 rounded-lg font-semibold hover:bg-primary-600 transition disabled:opacity-50 mb-3"
            >
              متابعة
            </button>
            <button
              type="button"
              onClick={goBack}
              className="w-full border border-gray-200 text-gray-600 py-3 rounded-lg font-semibold hover:bg-gray-50 transition"
            >
              رجوع
            </button>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={submitReset}>
            <h2 className="text-lg font-bold text-gray-800 mb-2 text-center">كلمة المرور الجديدة</h2>
            <p className="text-xs text-gray-500 text-center mb-6">اختر كلمة مرور قوية لا تقل عن 6 أحرف</p>

            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">كلمة المرور الجديدة</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                autoFocus
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm text-gray-600 mb-2">تأكيد كلمة المرور</label>
              <input
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder="••••••••"
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {remainingAttempts !== null && (
              <p className="text-xs text-red-600 mb-4 text-center">
                المحاولات المتبقية للرمز: {remainingAttempts}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-500 text-white py-3 rounded-lg font-semibold hover:bg-primary-600 transition disabled:opacity-50 mb-3"
            >
              {loading ? 'جاري الحفظ...' : 'تغيير كلمة المرور'}
            </button>
            <button
              type="button"
              onClick={goBack}
              disabled={loading}
              className="w-full border border-gray-200 text-gray-600 py-3 rounded-lg font-semibold hover:bg-gray-50 transition disabled:opacity-50"
            >
              رجوع
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
